<script setup lang="ts">
import type { IKYCLevel, TKYCStatus } from "@/app/shared/types/KYCLevels.type"
import { fetchRedirectUrlKyc, fetchUserKycLevels } from "@/app/api"
import PersonalInfoModal from "@/app/components/modal/PersonalInfoModal.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useI18n } from "@/app/composables/useI18n"
import { CKYC_STATUSES } from "@/app/shared/types/KYCLevels.type"
import { toast } from "@/app/utils/notification"
import { onMounted, ref } from "vue"

const kycLevels = ref<IKYCLevel[]>([])
const { t } = useI18n()
const isLoading = ref(false)
const showPersonalInfoModal = ref(false)

async function loadData(): Promise<void> {
  const { data } = await fetchUserKycLevels()
  // Only show levels 0, 1, and 2 for now
  kycLevels.value = (data.data || []).filter(level => level.level <= 2)
}

function getStatusText(status: TKYCStatus): string {
  const statusMap: Record<TKYCStatus, string> = {
    [CKYC_STATUSES.NOT_SUBMITTED]: t("responsible_gambling.pending") || "Not Started",
    [CKYC_STATUSES.SUBMITTED]: t("responsible_gambling.pending") || "Under Review",
    [CKYC_STATUSES.APPROVED]: t("responsible_gambling.active") || "Completed",
    [CKYC_STATUSES.REJECTED]: t("kyc.rejected") || "Rejected",
  }
  return statusMap[status] || t("kyc.unknown") || "Unknown"
}

function getStatusClass(status: TKYCStatus): string {
  const classMap: Record<TKYCStatus, string> = {
    [CKYC_STATUSES.NOT_SUBMITTED]: "status-pending",
    [CKYC_STATUSES.SUBMITTED]: "status-pending",
    [CKYC_STATUSES.APPROVED]: "status-active",
    [CKYC_STATUSES.REJECTED]: "status-deletion",
  }
  return classMap[status] || "status-unknown"
}

function getLevelDescription(level: number): string {
  const descriptions: Record<number, string> = {
    0: t("kyc.level_0_description") || "Basic account setup",
    1: t("kyc.level_1_description") || "Submit Personal Information",
    2: t("kyc.level_2_description") || "Submit basic KYC information",
  }
  return descriptions[level] || ""
}

function canTakeAction(level: number, status: TKYCStatus): boolean {
  if (level === 0)
    return false

  if (level === 1) {
    // Always allow editing personal info for Level 1, regardless of status
    return true
  }

  if (level === 2) {
    const level1 = kycLevels.value.find(l => l.level === 1)
    const isLevel1Approved = level1?.status === CKYC_STATUSES.APPROVED
    return isLevel1Approved && (status === CKYC_STATUSES.NOT_SUBMITTED || status === CKYC_STATUSES.REJECTED)
  }

  return false
}

function getActionIcon(level: number): string {
  if (level === 1)
    return "user"
  if (level === 2)
    return "document"
  return "arrow-right"
}

function getActionText(level: number, status: TKYCStatus): string {
  if (level === 1) {
    if (status === CKYC_STATUSES.APPROVED)
      return t("kyc.edit_info") || "Edit Info"
    if (status === CKYC_STATUSES.REJECTED)
      return t("kyc.resubmit") || "Resubmit"
    return t("kyc.start_level_1") || "Complete Info"
  }

  if (level === 2) {
    if (status === CKYC_STATUSES.REJECTED)
      return t("kyc.resubmit") || "Resubmit"
    return t("kyc.start_level_2") || "Start Verification"
  }

  return t("kyc.start") || "Start"
}

function openPersonalInfoModal(): void {
  showPersonalInfoModal.value = true
}

async function handleSumSubVerification(): Promise<void> {
  try {
    isLoading.value = true
    const response = await fetchRedirectUrlKyc()

    if (response.data?.url) {
      // window.open(response.data.url, "_blank", "noopener,noreferrer")
      const newTab = window.open(response.data.url, "_blank")
      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        // Fallback: open in the same tab if popup was blocked
        window.location.href = response.data.url
      }
      toast.info(t("kyc.verification_opened") || "Verification window opened")
    }
    else {
      toast.error(t("kyc.verification_error") || "Failed to open verification")
    }
  }
  catch (error) {
    console.error("Failed to get SumSub URL:", error)
    toast.error(t("kyc.verification_error") || "Failed to open verification")
  }
  finally {
    isLoading.value = false
  }
}

function handleLevelAction(level: number): void {
  if (level === 1) {
    openPersonalInfoModal()
  }
  else if (level === 2) {
    handleSumSubVerification()
  }
}

function onPersonalInfoSuccess(): void {
  showPersonalInfoModal.value = false
  loadData() // Refresh data after successful submission
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <div class="container">
    <div class="form">
      <div class="title">
        {{ t("general.kyc") || "KYC Verification" }}
      </div>
      <div class="subtitle">
        {{ t("kyc.verification_description") || "Complete your identity verification to unlock all features" }}
      </div>

      <div v-if="isLoading" class="loading">
        <p>{{ t("finance.loading") || "Loading..." }}</p>
      </div>

      <div v-else-if="kycLevels.length === 0" class="no-levels">
        <p>{{ t("kyc.no_levels_available") || "No KYC levels available." }}</p>
      </div>

      <div v-else class="kyc-list">
        <div
          v-for="level in kycLevels"
          :key="level.id"
          class="kyc-item"
        >
          <div class="kyc-info">
            <div class="kyc-header">
              <div class="kyc-name">
                {{ t("account.verification_level") }} {{ level.level }}
              </div>
              <div class="kyc-status" :class="getStatusClass(level.status)">
                {{ getStatusText(level.status) }}
              </div>
            </div>
            <div class="kyc-description">
              {{ getLevelDescription(level.level) }}
            </div>
          </div>

          <div class="kyc-actions">
            <div v-if="canTakeAction(level.level, level.status)" class="action-buttons">
              <BaseButton
                class="action-btn"
                :disabled="isLoading"
                @click="handleLevelAction(level.level)"
              >
                {{ getActionText(level.level, level.status) }}
              </BaseButton>
            </div>
            <div v-else-if="level.status === CKYC_STATUSES.APPROVED && level.level !== 1" class="completed-indicator">
              <BaseIcon name="check" size="16" color="#4caf50" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Personal Info Modal -->
    <PersonalInfoModal
      :show="showPersonalInfoModal"
      @close="showPersonalInfoModal = false"
      @success="onPersonalInfoSuccess"
    />
  </div>
</template>

<style lang="scss" scoped>
.container {
  .form {
    border-radius: 8px;
    background: var(--be-big-card-bg);
    padding: 16px;
    margin-bottom: 1rem;

    .title {
      font-size: 16px;
      font-weight: 600;
      color: var(--be-primary-text-color);
    }

    .subtitle {
      margin-top: 10px;
      font-size: 14px;
      font-weight: 400;
      color: var(--be-primary-text-color);
    }

    .no-levels, .loading {
      text-align: center;
      padding: 20px;
      color: var(--be-text-secondary);
      font-style: italic;
    }

    .kyc-list {
      margin-top: 10px;
      margin-bottom: 20px;

      .kyc-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 16px;
        margin-bottom: 12px;
        border-radius: 8px;
        background: var(--be-small-card-bg);
        border: 1px solid var(--be-border-color);

        .kyc-info {
          flex: 1;
          text-align: left;

          .kyc-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 4px;

            .kyc-name {
              font-size: 16px;
              font-weight: 600;
              color: var(--be-primary-text-color);
              text-align: left;
            }

            .kyc-status {
              font-size: 10px;
              font-weight: 500;
              padding: 2px 8px;
              border-radius: 12px;
              text-transform: uppercase;

              &.status-active {
                background: #4caf50;
                color: white;
              }

              &.status-pending {
                background: #ff9800;
                color: white;
              }

              &.status-deletion {
                background: #f44336;
                color: white;
              }

              &.status-unknown {
                background: #9e9e9e;
                color: white;
              }
            }
          }

          .kyc-description {
            font-size: 14px;
            color: var(--be-text-secondary);
            margin-bottom: 4px;
            text-align: left;
          }
        }

        .kyc-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-shrink: 0;

          .action-buttons {
            display: flex;
            gap: 8px;

            .action-btn {
              min-height: 32px;
              padding: 6px 16px;
              border-radius: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--be-primary-btn);
              color: var(--be-primary-btn-label);
              font-size: 12px;
              font-weight: 500;
              white-space: nowrap;

              &:hover {
                background: var(--be-primary-btn-hover, #2c6bc4);
              }

              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            }
          }

          .completed-indicator {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .kyc-list {
    .kyc-item {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
      padding: 20px 16px;

      .kyc-info {
        width: 100%;

        .kyc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;

          .kyc-name {
            font-size: 16px;
            text-align: left;
          }

          .kyc-status {
            flex-shrink: 0;
          }
        }

        .kyc-description {
          text-align: left;
          margin-bottom: 0;
        }
      }

      .kyc-actions {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .action-buttons {
          flex: 1;
          display: flex;
          gap: 12px;

          .action-btn {
            min-height: 44px;
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
            text-align: center;
          }
        }

        .completed-indicator {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-left: auto;
        }
      }
    }
  }
}

@media screen and (max-width: 500px) {
  .container {
    height: 100%;

    .form {
      padding: 12px;
    }
  }

  .kyc-list {
    .kyc-item {
      padding: 16px 12px;

      .kyc-actions {
        .action-buttons {
          gap: 8px;

          .action-btn {
            min-height: 36px;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 13px;
          }
        }

        .completed-indicator {
          width: 40px;
          height: 40px;
        }
      }
    }
  }
}
</style>
